import * as recallApi from '@otter-ai/recall-client';
import { useMutation } from '@tanstack/react-query';
import queryString from 'query-string';
import { APP_ID } from '@/constants/constant';
import type { CreateUserType } from '@/shared/types/auth';
import { currentTimeZone } from '@/shared/utils/time/timezone';

async function generateSignature(
  message: string,
  secret: string,
): Promise<string> {
  const enc = new TextEncoder();

  const key = await crypto.subtle.importKey(
    'raw',
    enc.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );

  const signature = await crypto.subtle.sign('HMAC', key, enc.encode(message));

  return Array.from(new Uint8Array(signature))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

type CreateUserArgs = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  referralCode?: string;
};

export const useCreateUserMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (res: CreateUserType) => void;
  onError?: (error: Error) => void;
}) => {
  return useMutation({
    mutationFn: async ({
      email,
      password,
      firstName,
      lastName,
      fullName,
      referralCode,
    }: CreateUserArgs) => {
      if (!process.env.NEXT_PUBLIC_CREATE_USER_SIGNIN_ALGORITHM) {
        throw new Error('/create_user signin algorithm is not configured.');
      }

      if (!process.env.NEXT_PUBLIC_CREATE_USER_SIGNIN_SECRET) {
        throw new Error('/create_user signin secret is not configured.');
      }

      const algorithm = process.env.NEXT_PUBLIC_CREATE_USER_SIGNIN_ALGORITHM;
      const secret = process.env.NEXT_PUBLIC_CREATE_USER_SIGNIN_SECRET;

      const ts = (Date.now() / 1000).toFixed(0);
      const message = queryString.stringify(
        { algorithm, email, password, ts },
        { encode: false },
      );

      const signature = await generateSignature(message, secret);

      const body = {
        email,
        password,
        algorithm,
        signature: signature,
        tz_str: currentTimeZone,
        referral_code: referralCode,
        ts,
        ...(fullName
          ? { full_name: fullName }
          : { first_name: firstName, last_name: lastName }),
      };

      const query = {
        appid: APP_ID,
        username: email,
      };

      const response = await recallApi.createUser({
        query,
        body,
        throwOnError: true,
      });

      return response.data;
    },
    onSuccess,
    onError,
  });
};
